import React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import HelmetIcon from "@/components/HelmetIcon";
import TeamLogo from "@/components/TeamLogo";
import { BarChart3, Users, Trophy, Flame } from "lucide-react";
import { getNativePlatform } from "@/lib/runtime";

function formatTeamName(teamKey, match, stats) {
  const fallback = teamKey === "home" ? match?.home_team : match?.away_team;
  return stats?.teams?.[teamKey]?.name || fallback || teamKey;
}

function getGateHelmetMeta(gate) {
  switch (String(gate)) {
    case "1":
      return { color: "#DC2626", label: "Röd" };
    case "2":
      return { color: "#2563EB", label: "Blå" };
    case "3":
      return { color: "#FFFFFF", label: "Vit" };
    case "4":
      return { color: "#FACC15", label: "Gul" };
    default:
      return { color: "#DC2626", label: "" };
  }
}

function StatCard({ label, value, helper, highlight = false, className }) {
  return (
    <div
      className={cn(
        "native-v3-match-stat flex w-full flex-col items-start justify-center gap-0.5 border border-border/70 bg-card px-3 py-3 text-left",
        highlight && "border-primary/35 bg-primary/[0.06]",
        className
      )}
    >
      <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
      <p className="text-xl font-semibold leading-none text-foreground sm:text-2xl">{value}</p>
      {helper ? <p className="text-[11px] text-muted-foreground">{helper}</p> : null}
    </div>
  );
}

function GateWinsChart({ gates, bestGates }) {
  const maxWins = React.useMemo(() => {
    if (!gates.length) return 0;
    return Math.max(...gates.map(([, wins]) => wins));
  }, [gates]);

  if (!gates.length) return null;

  return (
    <div className="native-v3-gate-chart flex items-end gap-3">
      {gates.map(([gate, wins]) => {
        const heightPercent = maxWins ? (wins / maxWins) * 100 : 0;
        const barHeight = wins > 0 ? Math.max(heightPercent, 12) : 0;
        const isBestGate = bestGates.includes(gate);

        return (
          <div key={gate} className="native-v3-gate-chart-column flex flex-1 flex-col items-center gap-1.5">
            {isBestGate ? (
              <p className="text-[11px] font-medium text-primary">Bästa startspår</p>
            ) : (
              <div className="h-[15px]" />
            )}
            <span className="text-xs font-semibold text-foreground">{wins}</span>
            <div
              className={cn(
                "native-v3-gate-chart-track flex h-28 w-full max-w-[56px] items-end justify-center border border-border/60 bg-background/70 p-1.5 transition-colors",
                isBestGate && "border-primary/70 bg-primary/10"
              )}
            >
              <div
                className={cn("native-v3-gate-chart-bar w-7 bg-primary transition-[height]", !isBestGate && "bg-primary/70")}
                style={{ height: `${barHeight}%` }}
                aria-hidden
              />
            </div>
            <div className="flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <HelmetIcon
                color={getGateHelmetMeta(gate).color}
                className="h-4 w-4"
                decorative={false}
                title={`Gate ${gate} – ${getGateHelmetMeta(gate).label}`}
              />
              <span>Gate {gate}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function RiderList({ team }) {
  const riders = Object.values(team?.riders || {}).sort((a, b) => {
    if (b.total !== a.total) return b.total - a.total;
    return (a.name || "").localeCompare(b.name || "", "sv");
  });

  if (!riders.length) {
    return <p className="text-sm text-muted-foreground">Inga registrerade poäng ännu.</p>;
  }

  return (
    <ul className="native-v3-match-rider-list space-y-2">
      {riders.map((rider) => (
        <li
          key={rider.id}
          className="native-v3-match-rider-row flex flex-col gap-2 border border-border/70 bg-background/80 p-3 text-sm sm:flex-row sm:items-center sm:justify-between"
        >
          <div className="min-w-0 space-y-1 text-left">
            <p className="truncate font-medium text-foreground">{rider.name}</p>
            <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
              <span>Matchpoäng {rider.base}</span>
              <span>Bonus {rider.bonus}</span>
              <span>Totalt {rider.total}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 text-left sm:text-right">
            <Badge variant="secondary" className="native-v3-match-stat-badge px-2.5 py-1 text-[11px]">
              {rider.base}+{rider.bonus}
            </Badge>
            <Badge className="native-v3-match-stat-badge bg-primary px-2.5 py-1 text-[11px] text-primary-foreground">
              {rider.total} p
            </Badge>
          </div>
        </li>
      ))}
    </ul>
  );
}

export default function MatchStatsDialog({ open, onClose, match, stats }) {
  const isNativeAndroid = getNativePlatform() === "android";
  const [activeTab, setActiveTab] = React.useState("overview");
  const handleOpenChange = (next) => {
    if (!next && onClose) onClose();
  };

  React.useEffect(() => {
    if (open) setActiveTab("overview");
  }, [open]);

  if (!match) return null;

  const gateWins = stats?.gateWins || {};
  const bestGates = stats?.bestGates || [];
  const heatOutcomes = stats?.heatOutcomes || { home: 0, away: 0, draws: 0 };
  const fiveOneHeats = stats?.fiveOneHeats || { home: 0, away: 0 };
  const exclusions = stats?.exclusions || 0;
  const sortedGates = Object.entries(gateWins).sort((a, b) => a[0].localeCompare(b[0], "sv"));

  const OverviewSection = (
    <div className="space-y-4">
      {sortedGates.length > 0 ? (
        <section className="native-v3-match-stats-section space-y-3 border border-border/70 bg-card/90 p-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="secondary" className="native-v3-match-stats-kicker gap-1 px-2.5 py-1">
              <Trophy className="h-3.5 w-3.5" />
              Startspår
            </Badge>
            {bestGates.length > 0 ? (
              bestGates.map((gate) => (
                <Badge key={gate} className="bg-primary text-primary-foreground">
                  Gate {gate}
                </Badge>
              ))
            ) : (
              <Badge className="bg-muted text-muted-foreground">Ingen tydlig favorit ännu</Badge>
            )}
          </div>
          <GateWinsChart gates={sortedGates} bestGates={bestGates} />
        </section>
      ) : null}

      <section className="native-v3-match-stats-section space-y-3 border border-border/70 bg-card/90 p-4">
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="native-v3-match-stats-kicker gap-1 px-2.5 py-1">
            <Flame className="h-3.5 w-3.5" />
            Heatutfall
          </Badge>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <StatCard label={formatTeamName("home", match, stats)} value={heatOutcomes.home} helper="heatsegrar" />
          <StatCard label={formatTeamName("away", match, stats)} value={heatOutcomes.away} helper="heatsegrar" />
          <StatCard label="3-3" value={heatOutcomes.draws} helper="antal heat" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <StatCard label={formatTeamName("home", match, stats)} value={fiveOneHeats.home} helper="5–1-resultat" />
          <StatCard label={formatTeamName("away", match, stats)} value={fiveOneHeats.away} helper="5–1-resultat" />
        </div>
        <div className="max-w-full">
          <StatCard label="Uteslutningar" value={exclusions} helper="totalt i matchen" className="w-full" />
        </div>
      </section>
    </div>
  );

  const TeamSection = (
    <div className="space-y-4">
      <section className="space-y-4">
        <div className="grid gap-5 md:grid-cols-2">
          {["home", "away"].map((teamKey) => {
            const team = stats?.teams?.[teamKey];
            if (!team) return null;
            const teamName = formatTeamName(teamKey, match, stats);

            return (
              <div key={teamKey} className="native-v3-match-team-section space-y-4 border border-border/70 bg-card/90 p-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <TeamLogo
                      teamName={teamName}
                      size={40}
                      className="h-10 w-10 shrink-0 object-contain"
                    />
                    <div className="min-w-0">
                      <h3 className="truncate text-lg font-semibold text-foreground">{teamName}</h3>
                      <p className="text-xs text-muted-foreground">Lagstatus och förarpoäng</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <StatCard label="Matchpoäng" value={team.base} />
                    <StatCard label="Bonus" value={team.bonus} />
                    <StatCard label="Totalt" value={team.total} highlight />
                  </div>
                </div>
                <RiderList team={team} />
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className={cn(
        "w-[calc(100vw-1rem)] max-w-full p-0 md:w-auto md:max-w-3xl md:p-5",
        isNativeAndroid && "native-v3-match-stats"
      )}>
        <div className="native-v3-match-stats-shell flex max-h-[calc(100vh-4rem)] flex-col md:max-h-[calc(100vh-6rem)]">
          <DialogHeader className={cn("box-border border-b border-border/60 p-4 pb-3 md:pt-0", isNativeAndroid && "native-v3-match-stats-header")}>
            <DialogTitle className="text-lg">Matchstatistik</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              {match.home_team} vs {match.away_team}
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="native-v3-match-stats-scroll box-border flex-1 max-w-full overflow-x-hidden px-4 pb-5 pt-3 md:px-0 md:pb-0">
            <div className="space-y-4 pb-3 md:pb-2">
              <Tabs value={activeTab} onValueChange={setActiveTab} className={cn("w-full", isNativeAndroid && "native-v3-match-stats-tabs")}>
                <div className="sticky top-0 z-10 -mx-4 mb-3 bg-background/95 px-4 pb-2 backdrop-blur supports-[backdrop-filter]:bg-background/85 md:mx-0 md:px-0">
                  <TabsList className="grid h-auto w-full grid-cols-2 gap-1 rounded-2xl border border-border bg-card p-1 shadow-[0_8px_24px_rgba(15,23,42,0.08)] dark:border-border/80 dark:bg-card dark:shadow-[0_10px_28px_rgba(0,0,0,0.35)]">
                    <TabsTrigger
                      value="overview"
                      className="rounded-xl px-3 py-2 text-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                    >
                      <BarChart3 className="mr-2 h-4 w-4" />
                      Heatstatistik
                    </TabsTrigger>
                    <TabsTrigger
                      value="teams"
                      className="rounded-xl px-3 py-2 text-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                    >
                      <Users className="mr-2 h-4 w-4" />
                      Lag &amp; förare
                    </TabsTrigger>
                  </TabsList>
                </div>

                <TabsContent value="overview" className="space-y-4">
                  <div className="flex items-center gap-2">
                    <BarChart3 className="h-4 w-4 text-primary" />
                    <h3 className="text-base font-semibold text-foreground">Gate &amp; heatstatistik</h3>
                  </div>
                  {OverviewSection}
                </TabsContent>

                <TabsContent value="teams" className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-primary" />
                    <h3 className="text-base font-semibold text-foreground">Lag &amp; förare</h3>
                  </div>
                  {TeamSection}
                </TabsContent>
              </Tabs>
            </div>
            <ScrollBar orientation="vertical" className="hidden md:flex" />
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
}
